import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { DrawOutlined as DrawOutlinedIcon } from "@mui/icons-material";
import { SIGNATURE_DISCLOSURE_VERSION } from "./signatureDisclosure";

// Shared violet focus-border treatment used by LMS form inputs (matches
// SectionInputsModal2 / AiContentGeneratorModal).
const inputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

// Authoring modal for an acknowledgement-mode signature block. The author writes
// a statement (e.g. a policy acknowledgement); the employee reads it and signs.
// No PDF, no field placement.
function AcknowledgementConfigModal(props) {
  const { open, handleClose, existingData, handleSignatureDataSave } = props;

  const [title, setTitle] = useState("");
  const [statement, setStatement] = useState("");
  const [saveToProfile, setSaveToProfile] = useState(true);

  useEffect(() => {
    if (open) {
      setTitle(existingData?.title || "");
      setStatement(existingData?.acknowledgementText || "");
      setSaveToProfile(existingData?.saveToProfile ?? true);
    }
  }, [open, existingData]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70%",
    maxWidth: 720,
    maxHeight: "92vh",
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    overflowY: "auto",
  };

  const handleSave = () => {
    if (!statement.trim()) {
      toast.error("Please enter the statement employees will acknowledge");
      return;
    }
    handleSignatureDataSave({
      signatureMode: "acknowledgement",
      title: title.trim(),
      acknowledgementText: statement.trim(),
      saveToProfile,
      consentDisclosureVersion: SIGNATURE_DISCLOSURE_VERSION,
    });
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="acknowledgement-config-modal">
      <Box sx={style}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: "20px" }} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Configure Acknowledgement</h3>
                <p className="text-sm text-slate-600">
                  Employees read your statement and sign to acknowledge it
                </p>
              </div>
            </div>
            <button
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
              onClick={handleClose}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <TextField
              placeholder="e.g. Employee Handbook Acknowledgement"
              fullWidth
              variant="outlined"
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              InputProps={{ sx: { ...inputSx, height: "45px" } }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statement to acknowledge
            </label>
            <TextField
              placeholder="e.g. I confirm that I have read, understood, and agree to abide by the policies described in the Employee Handbook."
              fullWidth
              multiline
              rows={8}
              variant="outlined"
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              InputProps={{ sx: inputSx }}
            />
            <p className="text-xs text-gray-500 mt-2">
              This exact wording is recorded with each signature for your records.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
          <div className="flex justify-between items-center gap-4">
            {/* Keep a copy of the signed acknowledgement on the employee profile */}
            <label className="flex items-center gap-2 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={saveToProfile}
                  onChange={(e) => setSaveToProfile(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
              </div>
              <span className="text-sm font-medium text-gray-700">Keep a copy on the employee's profile</span>
            </label>
            <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!statement.trim()}
              className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                !statement.trim()
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700 text-white"
              }`}
            >
              Save Acknowledgement
            </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default AcknowledgementConfigModal;
